/* ============================================================
   Revam Predictive Search (GTG-013) — Shopify native predictive search.
   <revam-predictive-search> wraps a search input + a [data-predictive-search]
   results container. Fetches /search/suggest and injects the rendered
   `revam-predictive-search` section. Ported & simplified from Dawn.
   Requires revam-search-form.js (RevamSearchForm base) loaded first.
   ============================================================ */
(function () {
  if (!window.RevamSearchForm || customElements.get('revam-predictive-search')) return;

  class RevamPredictiveSearch extends window.RevamSearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allInstances = document.querySelectorAll('revam-predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.statusElement = this.querySelector('.predictive-search-status');
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();
      const newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.showDefaultSuggestions();
        return;
      }
      this.hideDefaultSuggestions();
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      const currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) {
        this.showDefaultSuggestions();
        return;
      }
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      // Don't auto-close inside the mobile overlay (it has explicit close controls).
      if (this.closest('.revam-search-overlay')) return;
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.showDefaultSuggestions();
      switch (event.code) {
        case 'ArrowUp': event.preventDefault(); this.switchOption('up'); break;
        case 'ArrowDown': event.preventDefault(); this.switchOption('down'); break;
        case 'Enter': this.selectOption(); break;
        case 'Escape': this.close(); this.input.blur(); break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    updateSearchForTerm(previousTerm, newTerm) {
      const el = this.querySelector('[data-predictive-search-search-for-text]');
      const text = el?.innerText;
      if (text) {
        if (previousTerm && (text.match(new RegExp(previousTerm, 'g'))?.length || 0) > 1) return;
        el.innerText = previousTerm ? text.replace(previousTerm, newTerm) : text;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      const moveUp = direction === 'up';
      const selectedElement = this.querySelector('[aria-selected="true"]');
      const allVisibleElements = Array.from(
        this.querySelectorAll('li, button.predictive-search__item')
      ).filter((el) => el.offsetParent !== null);
      if (moveUp && !selectedElement) return;

      let selectedIndex = allVisibleElements.indexOf(selectedElement);
      let activeIndex = 0;
      if (!moveUp && selectedElement) {
        activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
      } else if (moveUp) {
        activeIndex = selectedIndex <= 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
      }
      if (activeIndex === selectedIndex) return;

      const activeElement = allVisibleElements[activeIndex];
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      const selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selected) selected.click();
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLoading();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      const suggestUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
      fetch(
        `${suggestUrl}?q=${encodeURIComponent(searchTerm)}&section_id=revam-predictive-search&resources[limit]=10`,
        { signal: this.abortController.signal }
      )
        .then((response) => {
          if (!response.ok) { this.close(); throw new Error(response.status); }
          return response.text();
        })
        .then((text) => {
          const dom = new DOMParser().parseFromString(text, 'text/html');
          const section = dom.querySelector('#shopify-section-revam-predictive-search');
          const markup = section ? section.innerHTML : text;
          this.allInstances.forEach((inst) => { inst.cachedResults[queryKey] = markup; });
          this.renderSearchResults(markup);
        })
        .catch((error) => {
          if (error?.code === 20) return; // aborted
          this.close();
        });
    }

    setLoading() {
      this.setAttribute('loading', true);
    }

    renderSearchResults(resultsMarkup) {
      this.hideDefaultSuggestions();
      const inputId = this.input.getAttribute('id');
      const defaults = document.getElementById('default-suggestions-' + inputId);
      const defaultsClone = defaults ? defaults.cloneNode(true) : null;

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      if (defaultsClone) {
        this.predictiveSearchResults.insertBefore(defaultsClone, this.predictiveSearchResults.firstChild);
      }
      this.setAttribute('results', true);
      this.removeAttribute('loading');
      this.open();
    }

    getResultsMaxHeight() {
      const rect = this.input.getBoundingClientRect();
      this.resultsMaxHeight = window.innerHeight - rect.bottom - 24;
      return Math.max(240, this.resultsMaxHeight);
    }

    open() {
      if (!this.closest('.revam-search-overlay')) {
        this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
      }
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      if (!this.getQuery().length) this.showDefaultSuggestions();
    }

    showDefaultSuggestions() {
      const inputId = this.input.getAttribute('id');
      const defaults = document.getElementById('default-suggestions-' + inputId);
      const wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.style.display = 'none';
      const results = this.querySelector('#predictive-search-results');
      if (results) results.style.display = 'none';
      this.removeAttribute('results');
      this.removeAttribute('loading');
      if (defaults) defaults.classList.remove('hidden');
      if (!this.getAttribute('open')) this.open();
    }

    hideDefaultSuggestions() {
      const inputId = this.input.getAttribute('id');
      const defaults = document.getElementById('default-suggestions-' + inputId);
      if (defaults) defaults.classList.add('hidden');
      const wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.style.display = '';
      const results = this.querySelector('#predictive-search-results');
      if (results) results.style.display = '';
    }

    close(clearSearchTerm = false) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm = false) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('revam-predictive-search', RevamPredictiveSearch);
})();
